import { API, BACKEND_URL } from '@/config/api';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, CheckCircle, Clock, MapPin, Package, Plus, Trash2, XCircle } from 'lucide-react';
import CustomerNavigation from '@/components/CustomerNavigation';
import SEO from '@/components/SEO';

const MyOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/sample-orders`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Kunne ikke laste bestillinger');
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Er du sikker pa at du vil kansellere denne bestillingen?')) return;
    try {
      await axios.delete(`${API}/sample-orders/${orderId}`, { withCredentials: true });
      toast.success('Bestilling kansellert');
      fetchOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke kansellere bestilling');
    }
  };

  const statusConfig = {
    pending:   { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', icon: Clock, label: 'Venter' },
    approved:  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: CheckCircle, label: 'Godkjent' },
    rejected:  { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', icon: XCircle, label: 'Avslatt' },
    completed: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-500', icon: Package, label: 'Fullfort' },
    cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400', icon: XCircle, label: 'Kansellert' },
  };

  const filteredOrders = orders.filter(o => filter === 'all' || o.status === filter);
  const counts = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    approved: orders.filter(o => o.status === 'approved').length,
    completed: orders.filter(o => o.status === 'completed').length,
    rejected: orders.filter(o => o.status === 'rejected').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex">
        <CustomerNavigation />
        <main className="flex-1 min-h-screen">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500 text-sm">Laster bestillinger...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      <SEO title="Bestillinger" description="Mine provebestillinger" />
      <CustomerNavigation />
      <main className="flex-1 min-h-screen">
        <div className="max-w-6xl mx-auto px-6 py-8 pt-16 lg:pt-8 animate-fade-in">
          
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight mb-2" data-testid="my-orders-title">
                Mine bestillinger
              </h1>
              <p className="text-slate-500">Oversikt over dine provebestillinger</p>
            </div>
            <button
              onClick={() => navigate('/customer/order-sample')}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 hover:scale-[1.02] active:scale-[0.98] transition-all"
              data-testid="new-order-button"
            >
              <Plus className="w-4 h-4" />
              Ny bestilling
            </button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1">
            {[
              { key: 'all', label: 'Alle' },
              { key: 'pending', label: 'Venter' },
              { key: 'approved', label: 'Godkjent' },
              { key: 'completed', label: 'Fullfort' },
              { key: 'rejected', label: 'Avslatt' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                data-testid={`filter-${tab.key}`}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  filter === tab.key
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 text-xs ${filter === tab.key ? 'text-white/70' : 'text-slate-400'}`}>
                  {counts[tab.key] || 0}
                </span>
              </button>
            ))}
          </div>

          {/* Orders List */}
          {filteredOrders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">Ingen bestillinger</h3>
              <p className="text-sm text-slate-500 mb-6">
                {filter === 'all' ? 'Du har ingen bestillinger enna.' : `Ingen bestillinger med valgt filter.`}
              </p>
              {filter === 'all' && (
                <button
                  onClick={() => navigate('/customer/order-sample')}
                  className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-all"
                >
                  Opprett forste bestilling
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order, i) => {
                const sc = statusConfig[order.status] || statusConfig.pending;
                const StatusIcon = sc.icon;
                return (
                  <div 
                    key={order.id} 
                    data-testid={`order-card-${order.id}`}
                    className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-sky-200 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-base font-heading font-semibold text-slate-900">
                          {order.order_number}
                        </h3>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${sc.bg} ${sc.text} ${sc.border}`}>
                          <StatusIcon className="w-3 h-3" />
                          {sc.label}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {new Date(order.created_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {order.status === 'pending' && (
                          <button
                            onClick={() => cancelOrder(order.id)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            title="Kanseller bestilling"
                            data-testid={`cancel-order-${order.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs text-slate-400 font-medium mb-1">Provetype</div>
                        <div className="text-sm font-medium text-slate-700">
                          {order.sample_type === 'algae' ? 'Algeprove' : 'Zooplankton'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-medium mb-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Lokalitet
                        </div>
                        <div className="text-sm text-slate-700">{order.location_name}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-medium mb-1 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Onsket dato
                        </div>
                        <div className="text-sm text-slate-700">
                          {new Date(order.preferred_date).toLocaleDateString('nb-NO')}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-medium mb-1">Dybde</div>
                        <div className="text-sm text-slate-700">{order.depth || '-'}</div>
                      </div>
                    </div>

                    {order.special_instructions && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="text-xs text-slate-400 font-medium mb-1">Notater</div>
                        <div className="text-sm text-slate-600">{order.special_instructions}</div>
                      </div>
                    )}

                    {order.admin_remarks && order.status !== 'pending' && (
                      <div className="mt-4 pt-4 border-t border-slate-100 bg-sky-50/50 -mx-6 -mb-6 px-6 py-4 rounded-b-2xl">
                        <div className="text-xs text-sky-600 font-medium mb-1">Tilbakemelding fra lab</div>
                        <div className="text-sm text-sky-800">{order.admin_remarks}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MyOrders;
